/**
 * OpenSoi API client.
 *
 * The backend lives at $VITE_API_BASE_URL (production: https://api.opensoi.app).
 * Auth uses Firebase ID tokens passed as `Authorization: Bearer <token>`.
 * Errors come back in a bilingual envelope:
 *   { error: { code, message_en, message_th } }
 */

import i18n from '@/i18n';

/**
 * Resolved at call time (not import time) so tests can stub env via vi.stubEnv
 * before the first request. In production this is read from the build-time
 * VITE_API_BASE_URL embedded in the bundle.
 */
export function getApiBase(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL ?? '').toString().trim();
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

export interface ApiError extends Error {
  status: number;
  code?: string;
  message_en?: string;
  message_th?: string;
}

/**
 * Wraps non-HTTP failures (DNS, TLS, CORS, redirect-blocked, generic
 * "Failed to fetch"). Preserves the underlying error as `cause` so devtools
 * still show the real reason. Callers should map this to a user-facing
 * "couldn't reach the server" string.
 */
export class ApiNetworkError extends Error {
  constructor(cause: unknown) {
    super('network_error');
    this.name = 'ApiNetworkError';
    if (cause instanceof Error) this.cause = cause;
  }
}

export class ApiHttpError extends Error implements ApiError {
  status: number;
  code?: string;
  message_en?: string;
  message_th?: string;

  constructor(status: number, body: { code?: string; message_en?: string; message_th?: string }) {
    const lang = (typeof i18n.language === 'string' ? i18n.language : 'en').slice(0, 2);
    const message =
      lang === 'th'
        ? body.message_th ?? body.message_en ?? `HTTP ${status}`
        : body.message_en ?? body.message_th ?? `HTTP ${status}`;
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.code = body.code;
    this.message_en = body.message_en;
    this.message_th = body.message_th;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const base = getApiBase();
  if (!base) {
    throw new Error('VITE_API_BASE_URL is not configured');
  }
  if (!path.startsWith('/')) {
    throw new Error('apiFetch path must start with "/"');
  }
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers['Authorization'] = `Bearer ${opts.token}`;

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      // Never include credentials/cookies — we use Bearer tokens only.
      credentials: 'omit',
      // Don't follow redirects to attacker-controlled hosts silently.
      redirect: 'error',
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiNetworkError(e);
  }

  if (res.status === 204) {
    // 204 No Content — caller must treat the result as void. Returning
    // undefined cast to T is the conventional escape but means the caller's
    // generic must accept it (`T = void` or `T | undefined`). Avoid 204
    // endpoints in code paths whose response shape is non-trivial.
    return undefined as unknown as T;
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // fall through; treat as opaque
    }
  }

  if (!res.ok) {
    const errEnv =
      typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? (parsed as { error?: { code?: string; message_en?: string; message_th?: string } }).error
        : undefined;
    throw new ApiHttpError(res.status, errEnv ?? {});
  }

  return parsed as T;
}
